import * as React from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { postJSON } from '@/lib/api'
import type { OrderLink, OrderLinkResponse } from '@/pages/OrderLinkPage'

export function OrderCancellation({
  order,
  queryKey,
  cancelPath,
}: {
  order: OrderLink
  queryKey: readonly unknown[]
  cancelPath?: string
}) {
  const queryClient = useQueryClient()
  const [confirming, setConfirming] = React.useState(false)
  const cancel = useMutation({
    mutationFn: () => postJSON<OrderLinkResponse>(cancelPath ?? '', {}),
    onSuccess: (response) => {
      queryClient.setQueryData(queryKey, response)
      setConfirming(false)
    },
  })

  if (order.cancelled || order.event.cancelled || !cancelPath) return null

  if (!order.cancellable) {
    return order.cancellation_note ? (
      <p className="text-caption text-muted-foreground">{order.cancellation_note}</p>
    ) : null
  }

  if (!confirming) {
    return (
      <Card>
        <CardContent className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
          <div>
            <p className="text-body-sm font-medium">Need to make changes?</p>
            <p className="text-caption text-muted-foreground">
              Cancel this registration before the deadline.
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            className="h-8"
            onClick={() => setConfirming(true)}
          >
            Cancel my registration
          </Button>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardContent className="space-y-3 px-4 py-3">
        <p className="text-body-sm font-medium">Cancel this registration?</p>
        <p className="text-caption text-muted-foreground">
          Your Invoice will be voided and nothing will be owed.
        </p>
        {cancel.isError && <Alert>{cancel.error.message}</Alert>}
        <div className="flex gap-2">
          <Button
            type="button"
            className="h-8"
            onClick={() => cancel.mutate()}
            disabled={cancel.isPending}
          >
            {cancel.isPending ? 'Cancelling…' : 'Confirm cancellation'}
          </Button>
          <Button
            type="button"
            variant="outline"
            className="h-8"
            onClick={() => setConfirming(false)}
          >
            Keep registration
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
